
"use client";

import { AnimatedText } from "@/components/custom/animated-text";
import { MagneticButton } from "@/components/custom/magnetic-button";
import Link from "next/link";
import { ArrowUpRight, ArrowDown, Sparkles, TrendingUp, Zap, Printer, Plane, Layers, Globe, Triangle, Circle, Square, Box } from "lucide-react";
import { motion } from "framer-motion";
import { TiltCard } from "@/components/custom/tilt-card";
import { staggerContainer, fadeInUp } from "@/lib/animations";

const Marquee = () => {
	return (
		<div className="flex w-full items-center overflow-hidden bg-foreground py-3 text-background">
			<motion.div
				initial={{ x: 0 }}
				animate={{ x: "-50%" }}
				transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
				className="flex min-w-full whitespace-nowrap font-syne text-4xl font-bold uppercase tracking-widest md:text-6xl"
			>
				{[...Array(4)].map((_, i) => (
					<span key={i} className="mx-8">
						Strategy • Design • Content • Growth •
					</span>
				))}
			</motion.div>
		</div>
	);
};

export default function Home() {
	return (
		<div className="relative flex min-h-screen flex-col overflow-x-hidden bg-background font-sans text-foreground selection:bg-primary selection:text-primary-foreground">
			{/* Hero Section */}
			<section className="relative flex min-h-[90vh] flex-col justify-center px-4 py-20 md:px-10">
				<motion.div
					variants={staggerContainer}
					initial="hidden"
					animate="show"
					className="z-10 flex flex-col"
				>
					<h1 className="sr-only">TheHighrays - YouTube Growth & 3D Fabrication Services</h1>
					<AnimatedText
						text="THE"
						className="font-syne text-[15vw] font-bold leading-[0.8] tracking-tighter text-primary mix-blend-difference md:text-[11vw]"
						triggerOnMount={true}
						delay={0}
					/>
					<AnimatedText
						text="HIGHRAYS"
						className="font-syne text-[10vw] font-bold leading-[0.8] tracking-tighter text-primary mix-blend-difference md:text-[9vw] whitespace-nowrap"
						triggerOnMount={true}
						delay={0.1}
					/>
					{/* Desktop Layout for DIGITAL SUCCESS + CTA */}
					<div className="relative hidden md:block">
						<AnimatedText
							text="DIGITAL"
							className="font-syne text-[8vw] font-bold leading-[0.9] tracking-tighter opacity-50 md:text-[9vw]"
							triggerOnMount={true}
							delay={0.2}
						/>
						<div className="relative flex items-center gap-2">
							<AnimatedText
								text="SUCCESS"
								className="font-syne text-[8vw] font-bold leading-[0.9] tracking-tighter opacity-50 md:text-[9vw]"
								triggerOnMount={true}
								delay={0.3}
							/>
							{/* The "Unconscious" CTA */}
							<div className="relative md:left-20 md:top-40">
								<motion.div
									variants={fadeInUp}
									className="absolute -left-30 -top-30 rotate-12 hidden md:block pointer-events-none"
								>
									<svg
										width="200"
										height="150"
										viewBox="0 0 200 150"
										fill="none"
										xmlns="http://www.w3.org/2000/svg"
										className="animate-pulse text-primary"
									>
										<path
											d="M20 120 C 80 120, 120 80, 180 40"
											stroke="black"
											strokeWidth="2"
											fill="none"
										/>
										<path
											d="M180 40 L 170 52 M 180 40 L 166 46"
											stroke="black"
											strokeWidth="2"
											fill="none"
										/>
									</svg>
								</motion.div>
								<motion.div
									variants={fadeInUp}
									className="relative md:-top-46 md:left-20"
								>
									<Link href="https://www.thehighraysyt.com" prefetch={false} target="_blank">
										<MagneticButton className="group flex h-32 w-32 items-center justify-center rounded-full bg-[#FF0000] text-white transition-all hover:scale-110 md:h-48 md:w-48">
											<div className="z-10 flex flex-col items-center">
												<div className="flex items-center gap-1">
													<span className="font-syne text-xl font-bold md:text-2xl">
														YouTube
													</span>
													<ArrowUpRight className="h-6 w-6 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
												</div>
												<span className="text-sm font-medium opacity-90">Get Instant Views & Subs</span>
												<span className="text-xs opacity-90">Don't miss out!</span>
											</div>
											<div className="absolute inset-0 -z-10 animate-ping rounded-full bg-[#FF0000] opacity-20 duration-1000"></div>
										</MagneticButton>
									</Link>
								</motion.div>
							</div>
						</div>
					</div>

					{/* Mobile Layout for DIGITAL SUCCESS + CTA */}
					<div className="relative mt-0 flex flex-col items-start md:hidden">
						<AnimatedText
							text="DIGITAL"
							className="font-syne text-[15vw] font-bold leading-[0.8] tracking-tighter opacity-50"
							triggerOnMount={true}
							delay={0.2}
						/>
						<AnimatedText
							text="SUCCESS"
							className="font-syne text-[15vw] font-bold leading-[0.8] tracking-tighter opacity-50"
							triggerOnMount={true}
							delay={0.3}
						/>

						<motion.div variants={fadeInUp} className="relative mt-10 w-full pr-4">
							<Link href="https://www.thehighraysyt.com" prefetch={false} className="group block w-full" target="_blank">
								<div className="relative flex w-full overflow-hidden rounded-xl bg-zinc-100 text-zinc-950 transition-transform duration-300 active:scale-95 dark:bg-zinc-900 dark:text-zinc-50">
									{/* Left Side: Main Content */}
									<div className="flex-1 p-5">
										<div className="mb-2 flex items-center gap-2">
											<span className="inline-block h-2 w-2 rounded-full bg-red-500 animate-pulse"></span>
											<span className="font-mono text-[10px] font-bold uppercase tracking-widest text-zinc-600 dark:text-zinc-400">
												Click Here
											</span>
										</div>
										<h3 className="font-syne text-3xl font-bold uppercase leading-none tracking-tighter">
											YouTube<br />
											<span className="text-red-600">Promotion</span>
										</h3>
										<div className="mt-4 flex flex-wrap gap-1">
											{['VIEWS', 'SUBS', 'LIKES', 'WATCH_T'].map((tag) => (
												<span key={tag} className="border border-zinc-300 px-1.5 py-0.5 font-mono text-[10px] font-bold uppercase text-zinc-600 dark:text-zinc-400 dark:border-zinc-700">
													{tag}
												</span>
											))}
										</div>
									</div>

									{/* Divider / Perforation */}
									<div className="relative flex w-8 flex-col items-center justify-between border-l-2 border-dashed border-zinc-300 bg-zinc-200/50 py-2 dark:border-zinc-700 dark:bg-black/20">
										{/* Semi-circles for notches */}
										<div className="absolute -left-1.5 -top-1.5 h-3 w-3 rounded-full bg-background"></div>
										<div className="absolute -left-1.5 -bottom-1.5 h-3 w-3 rounded-full bg-background"></div>

										<div className="h-full w-px bg-transparent"></div> {/* Spacer */}
										<span className="rotate-90 whitespace-nowrap font-mono text-[10px] font-bold uppercase tracking-widest text-zinc-400">
											#8392-VIRAL
										</span>
									</div>

									{/* Right Side: Action Stub */}
									<div className="flex w-16 items-center justify-center bg-red-500 text-white transition-colors duration-300 group-hover:bg-red-600">
										<ArrowUpRight className="h-8 w-8 transition-transform duration-300 group-hover:-rotate-12" />
									</div>
								</div>
							</Link>
						</motion.div>
					</div>
				</motion.div>

				<div className="mt-10 flex w-full flex-col items-start justify-between gap-12 md:mt-20 md:flex-row md:items-end">
					<motion.div variants={fadeInUp} className="max-w-md space-y-6">
						<p className="font-inter text-lg font-medium leading-relaxed text-muted-foreground md:text-xl">
							We craft digital experiences that resonate. From branding to viral
							content, we are the architects of your online presence.
						</p>
						<div className="mb-10 flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-primary md:mb-0">
							<ArrowDown className="animate-bounce" />
							Scroll to explore
						</div>
					</motion.div>
				</div>

			</section>

			<Marquee />

			{/* Product Focus Section - TheHighRays (Style 4: Bauhaus / Geometric) */}
			<section id="brands" className="relative min-h-screen flex flex-col items-center justify-center bg-[#040900] px-4 py-20 text-white overflow-hidden border-t border-zinc-800">
				{/* Bauhaus Background */}
				<div className="absolute top-0 left-0 w-full h-full pointer-events-none opacity-10">
					<div className="absolute top-0 left-1/4 w-px h-full bg-gray-400"></div>
					<div className="absolute top-0 right-1/4 w-px h-full bg-gray-400"></div>
					<div className="absolute top-1/3 left-0 w-full h-px bg-gray-400"></div>
					<div className="absolute bottom-1/3 left-0 w-full h-px bg-gray-400"></div>
				</div>

				{/* Geometric Shapes */}
				<div className="absolute top-20 left-20 w-32 h-32 bg-red-600 rounded-full mix-blend-screen opacity-40 pointer-events-none"></div>
				<div className="absolute bottom-40 right-20 w-40 h-40 bg-blue-600 mix-blend-screen opacity-40 pointer-events-none clip-path-polygon-[50%_0%,0%_100%,100%_100%]"></div>
				<div className="absolute top-1/2 right-1/3 w-24 h-24 bg-yellow-500 mix-blend-screen opacity-40 pointer-events-none rotate-45"></div>

				<div className="container mx-auto z-10 relative">
					<div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
						<motion.div
							initial={{ opacity: 0, x: -50 }}
							whileInView={{ opacity: 1, x: 0 }}
							transition={{ duration: 0.8, ease: "circOut" }}
							viewport={{ once: true }}
							className="lg:col-span-5 flex flex-col items-start text-left relative"
						>
							<div className="mb-8 bg-white text-black px-4 py-2 font-syne font-bold uppercase tracking-widest text-sm inline-block transform -rotate-2">
								WORLDWIDE | CANADA
							</div>

							<h2 className="mb-8 font-syne text-5xl md:text-7xl lg:text-8xl font-bold leading-[0.9] tracking-tighter uppercase text-white">
								The <span className="text-red-600">Highrays</span> YT
							</h2>

							<p className="mb-10 font-inter text-xl text-zinc-300 leading-relaxed max-w-md font-medium border-l-4 border-yellow-500 pl-6">
								We manage your YouTube ads and strategy so you can focus on making videos that earn you the real dough.
							</p>

							<Link href="https://www.thehighraysyt.com" prefetch={false} target="_blank">
								<MagneticButton className="h-16 px-10 bg-blue-600 text-white font-syne text-lg font-bold uppercase tracking-widest hover:bg-white hover:text-black transition-colors duration-300 flex items-center gap-2 shadow-[4px_4px_0px_0px_rgba(255,255,255,0.2)] hover:shadow-none hover:translate-x-[2px] hover:translate-y-[2px]">
									Start Growing
									<ArrowUpRight className="h-5 w-5" />
								</MagneticButton>
							</Link>
						</motion.div>

						<motion.div
							initial={{ opacity: 0, scale: 0.9 }}
							whileInView={{ opacity: 1, scale: 1 }}
							transition={{ duration: 0.8, delay: 0.2 }}
							viewport={{ once: true }}
							className="lg:col-span-7 relative h-[70vh] lg:h-[90vh]"
						>
							{/* Floating Cards Composition - Full Set */}
							<div className="relative w-full h-full">
								{/* 1. Watch Time */}
								<motion.div
									animate={{ y: [0, -15, 0] }}
									transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
									className="absolute top-[5%] md:top-[20%] left-[10%] md:left-[20%] z-30 bg-white text-black border-2 border-black p-4 w-56 shadow-[4px_4px_0px_0px_rgba(255,204,0,1)]"
								>
									<div className="flex items-center gap-2 mb-2">
										<div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
										<span className="font-mono text-xs font-bold uppercase">WatchTime</span>
									</div>
									<div className="font-syne text-4xl font-bold">4,000h+</div>
									<div className="text-[10px] font-bold uppercase bg-black text-white px-1 inline-block mt-1">Monetization Ready</div>
								</motion.div>

								{/* 2. Subscribers */}
								<motion.div
									animate={{ y: [0, 20, 0], rotate: [0, 2, 0] }}
									transition={{ duration: 7, repeat: Infinity, ease: "easeInOut", delay: 1 }}
									className="absolute top-[24%] md:top-[40%] right-[5%] z-20 bg-white text-black border-2 border-black p-4 w-48 shadow-[4px_4px_0px_0px_rgba(220,38,38,1)] rotate-2"
								>
									<div className="flex items-center gap-2 mb-2">
										<div className="w-3 h-3 bg-red-600 rounded-full"></div>
										<span className="font-mono text-xs font-bold uppercase">Subscribers</span>
									</div>
									<div className="font-syne text-3xl font-bold">100K+</div>
									<div className="flex items-center gap-1 text-xs font-bold text-red-600 mt-1">
										<TrendingUp className="h-3 w-3" /> +12.5%
									</div>
								</motion.div>

								{/* 3. Total Views */}
								<motion.div
									animate={{ x: [0, 10, 0] }}
									transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
									className="absolute top-[45%] md:top-[50%] left-[5%] z-20 bg-white text-black border-2 border-black p-4 w-52 shadow-[4px_4px_0px_0px_rgba(37,99,235,1)] -rotate-1"
								>
									<div className="flex items-center gap-2 mb-2">
										<div className="w-3 h-3 bg-blue-600 rounded-full"></div>
										<span className="font-mono text-xs font-bold uppercase">Total Views</span>
									</div>
									<div className="font-syne text-3xl font-bold">1M+</div>
									<div className="w-full h-2 bg-zinc-200 mt-2 border border-black">
										<div className="h-full w-[85%] bg-blue-600"></div>
									</div>
								</motion.div>

								{/* 4. Engagement */}
								<motion.div
									animate={{ y: [0, -10, 0] }}
									transition={{ duration: 6.5, repeat: Infinity, ease: "easeInOut", delay: 1.5 }}
									className="absolute bottom-[3%] md:bottom-[15%] md:left-[20%] left-[10%] z-30 bg-black text-white p-4 w-48 shadow-[4px_4px_0px_0px_rgba(220,38,38,1)]"
								>
									<div className="flex items-center gap-2 mb-2">
										<div className="w-3 h-3 bg-green-500 rounded-full"></div>
										<span className="font-mono text-xs font-bold uppercase">Growth</span>
									</div>
									<div className="font-syne text-3xl font-bold">+458%</div>
								</motion.div>

								{/* 5. Comments */}
								<motion.div
									animate={{ rotate: [0, -3, 0] }}
									transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 2 }}
									className="absolute bottom-[20%] md:bottom-[25%] right-[15%] z-20 bg-yellow-400 text-black border-2 border-black p-4 w-40 shadow-[4px_4px_0px_0px_rgba(255,255,255,0.5)] rotate-3"
								>
									<div className="font-mono text-xs font-bold uppercase mb-1">Engagement</div>
									<div className="font-syne text-2xl font-bold">10K+</div>
									<div className="text-xs font-bold">Comments</div>
								</motion.div>
							</div>
						</motion.div>
					</div>
				</div>
			</section>

			{/* Elec3D Focus Section - (Style 4: Bauhaus / Geometric) */}
			<section className="relative min-h-screen flex flex-col items-center justify-center bg-[#e5e5e5] px-4 py-20 text-black overflow-hidden border-t border-black">
				{/* Geometric Background */}
				<div className="absolute inset-0 pointer-events-none">
					<div className="absolute top-0 right-0 w-1/2 h-full bg-white clip-path-polygon-[20%_0%,100%_0,100%_100%,0%_100%]"></div>
				</div>

				<div className="container mx-auto z-10 relative">
					<div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
						<motion.div
							initial={{ opacity: 0, x: 50 }}
							whileInView={{ opacity: 1, x: 0 }}
							transition={{ duration: 0.8, ease: "circOut" }}
							viewport={{ once: true }}
							className="lg:col-span-6 order-2 lg:order-1 relative h-[50vh] lg:h-[70vh] flex items-center justify-center"
						>
							{/* Simplified 3D/Drone Visuals */}
							<div className="relative w-full max-w-md aspect-square">
								{/* Main Circle */}
								<div className="absolute inset-0 rounded-full border-[20px] border-black flex items-center justify-center bg-transparent z-10"></div>

								{/* Inner Elements */}
								<motion.div
									animate={{ rotate: 360 }}
									transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
									className="absolute inset-4 border border-dashed border-black rounded-full z-0"
								></motion.div>

								{/* Drone Icon */}
								<motion.div
									animate={{ y: [-10, 10, -10] }}
									transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
									className="absolute top-1/4 left-1/4 z-20 bg-blue-600 text-white p-4 rounded-full shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]"
								>
									<Plane className="w-8 h-8" />
								</motion.div>

								{/* Printer Icon */}
								<motion.div
									animate={{ y: [10, -10, 10] }}
									transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 1 }}
									className="absolute bottom-1/4 right-1/4 z-20 bg-red-600 text-white p-4 rounded-none shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]"
								>
									<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-8 h-8">
										<path d="M4 21h16" />
										<path d="M5 21V7h14v14" />
										<path d="M9 16h6" />
										<path d="M12 7v5" />
										<rect x="10" y="12" width="4" height="3" />
									</svg>
								</motion.div>

								{/* Center Text */}
								<div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-30 bg-yellow-400 border-2 border-black px-4 py-2 font-syne font-bold uppercase text-xl shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] whitespace-nowrap">
									3D FAB /DRONE /ROBOTICS /ART /ANIME
								</div>
							</div>
						</motion.div>

						<motion.div
							initial={{ opacity: 0, x: -50 }}
							whileInView={{ opacity: 1, x: 0 }}
							transition={{ duration: 0.8, delay: 0.2 }}
							viewport={{ once: true }}
							className="lg:col-span-6 order-1 lg:order-2 flex items-center justify-center"
						>
							<div className="flex flex-col items-start text-left w-full max-w-lg">
								<div className="mb-6 flex items-center gap-3">
									<div className="w-4 h-4 bg-black"></div>
									<div className="w-4 h-4 bg-red-600 rounded-full"></div>
									<div className="w-4 h-4 bg-blue-600 clip-path-polygon-[50%_0%,0%_100%,100%_100%]"></div>
									<span className="font-mono text-sm font-bold uppercase tracking-widest ml-2">
										Form Follows Function
									</span>
								</div>

								<h2 className="mb-8 font-syne text-6xl md:text-8xl font-bold leading-[0.85] tracking-tighter uppercase text-black">
									Elec3D
								</h2>

								<p className="mb-10 font-inter text-xl text-zinc-800 leading-relaxed max-w-lg font-medium">
									Turning digital geometry into physical reality. We specialize in <span className="bg-yellow-300 px-1">3D fabrication</span>, <span className="bg-blue-200 px-1">autonomous drones</span>, and <span className="bg-red-200 px-1">artistic collectibles</span>.
								</p>

								<div className="flex flex-wrap gap-4 mb-12">
									<div className="px-4 py-2 border-2 border-black font-bold uppercase text-sm bg-white shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]">3D Fabrication</div>
									<div className="px-4 py-2 border-2 border-black font-bold uppercase text-sm bg-white shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]">Drone Systems</div>
									<div className="px-4 py-2 border-2 border-black font-bold uppercase text-sm bg-white shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]">Art/Sci-Fi</div>
								</div>

								<Link href="/brands/elec3d" prefetch={false} aria-label="Learn more about ELEC3D - 3D fabrication, drones, and collectibles">
									<MagneticButton className="h-16 px-10 bg-black text-white font-syne text-lg font-bold uppercase tracking-widest hover:bg-red-600 transition-colors duration-300 flex items-center gap-2 shadow-[4px_4px_0px_0px_rgba(220,38,38,1)] hover:shadow-none hover:translate-x-[2px] hover:translate-y-[2px]">
										Explore ELEC3D
										<ArrowUpRight className="h-5 w-5" />
									</MagneticButton>
								</Link>
							</div>
						</motion.div>
					</div>
				</div>
			</section>
		</div >
	);
}
